import { ArrowDirection, KeyboardNavigationMode } from '@hh.ru/magritte-common-keyboard/types';

const FLOAT_COMPARISON_THRESHOLD = 0.5;

export const FOCUSABLE_SELECTOR = `
    button:not([disabled]):not([tabindex="-1"]),
    a[href],
    input:not([disabled]):not([tabindex="-1"]),
    select:not([disabled]):not([tabindex="-1"]),
    textarea:not([disabled]):not([tabindex="-1"]),
    [tabindex]:not([tabindex="-1"])
`;

/**
 * Находит следующий фокусабельный элемент в контейнере
 * В зависимости от параметра `mode`:
 *  `list` — элемент ищется сначала в заданном направлении, потом с противоположной стороны контейнера (т.е. навигация зациклена)
 *  `main` — элемент ищется только в заданном направлении относительно таргета
 */
export const findNextFocusableElement = (
    container: Element,
    targetElement: Element | null,
    direction: ArrowDirection,
    mode: KeyboardNavigationMode = 'main'
): HTMLElement | null => {
    if (
        (mode === 'main' && !targetElement) ||
        (targetElement !== null && (targetElement === container || !container.contains(targetElement)))
    ) {
        return null;
    }

    const focusableElements = [...container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)];

    const targetRect = targetElement?.getBoundingClientRect() ?? null;
    const targetCenterX = targetRect !== null ? Math.round(targetRect.left + targetRect.width / 2) : Number.NaN;
    const targetCenterY = targetRect !== null ? Math.round(targetRect.top + targetRect.height / 2) : Number.NaN;

    let nearestElement: HTMLElement | null = null;
    let minDistance = Infinity;

    let oppositeElement: HTMLElement | null = null;
    let oppositeCoordinate = Number.NaN;

    switch (direction) {
        case 'up':
            oppositeCoordinate = targetRect?.top ?? 0;
            break;
        case 'down':
            oppositeCoordinate = targetRect?.bottom ?? Infinity;
            break;
        case 'left':
            oppositeCoordinate = targetRect?.right ?? 0;
            break;
        case 'right':
            oppositeCoordinate = targetRect?.left ?? Infinity;
            break;
    }

    focusableElements.forEach((element) => {
        if (targetElement === element) {
            return;
        }

        const elementRect = element.getBoundingClientRect();
        const elementCenterX = Math.round(elementRect.left + elementRect.width / 2);
        const elementCenterY = Math.round(elementRect.top + elementRect.height / 2);

        // Ищем ближайший к таргету элемент в заданном направлении
        if (
            targetRect !== null &&
            !(direction === 'up' && (elementCenterY >= targetCenterY || elementRect.bottom > targetRect.top)) &&
            !(direction === 'down' && (elementCenterY <= targetCenterY || elementRect.top < targetRect.bottom)) &&
            !(direction === 'left' && (elementCenterX >= targetCenterX || elementRect.right > targetRect.left)) &&
            !(direction === 'right' && (elementCenterX <= targetCenterX || elementRect.left < targetRect.right))
        ) {
            let distanceX = Math.abs(targetCenterX - elementCenterX);
            let distanceY = Math.abs(targetCenterY - elementCenterY);

            // Добавляем коэффициенты для соответствующих осей,
            // чтобы сделать элементы в направлении, соответствующем нажатой arrow-клавише, более приоритетными
            if (direction === 'up' || direction === 'down') {
                distanceY *= 0.5;
            } else {
                distanceX *= 0.5;
            }

            const distance = Math.hypot(distanceX, distanceY);

            if (distance < minDistance) {
                minDistance = distance;
                nearestElement = element;
            }
        }

        // Ищем ближайший к противоположной стороне элемент
        if (mode === 'list') {
            if (direction === 'up' && elementRect.top - oppositeCoordinate > FLOAT_COMPARISON_THRESHOLD) {
                oppositeElement = element;
                oppositeCoordinate = elementRect.top;
            } else if (direction === 'down' && elementRect.bottom - oppositeCoordinate < -FLOAT_COMPARISON_THRESHOLD) {
                oppositeElement = element;
                oppositeCoordinate = elementRect.bottom;
            } else if (direction === 'left' && elementRect.right - oppositeCoordinate > FLOAT_COMPARISON_THRESHOLD) {
                oppositeElement = element;
                oppositeCoordinate = elementRect.right;
            } else if (direction === 'right' && elementRect.left - oppositeCoordinate < -FLOAT_COMPARISON_THRESHOLD) {
                oppositeElement = element;
                oppositeCoordinate = elementRect.left;
            }
        }
    });

    return nearestElement ?? oppositeElement;
};
